/**
 * SETTINGS PAGE - STREAMLINED WITH SECTION SAVES
 * 
 * User account settings including:
 * - Username (with 30-day change cooldown)
 * - Privacy settings (what's visible on public profile)
 * - Appearance (theme, items per page)
 * - Notifications
 * - Account management (change password, delete account)
 * 
 * Each section has its own save button!
 * 
 * NOTE: Display Name, Bio, Avatar, Banner, and Social Links
 * are edited via EditProfileModal on the Profile page.
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, deleteField, collection, getDocs, Timestamp } from 'firebase/firestore';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential, onAuthStateChanged, signOut, verifyBeforeUpdateEmail } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebase/config';
import { getCurrentUser, sendVerificationEmail } from '../firebase/auth';
import NotificationSettings from '../components/NotificationSettings';
import { NotificationPrompt } from '../components/NotificationPrompt';
import { AlertCircle, Check, Loader2, Settings as SettingsIcon } from 'lucide-react';
import { PageHeader } from '../components/common';
import StorageUsageCard from '../components/StorageUsageCard';
import StorageUpgradeModal from '../components/StorageUpgradeModal';
import type { StorageTierId } from '../constants/storageTiers';
import { SUPPORTED_COUNTRIES, groupCountriesByRegion, defaultCurrencyForCountry } from '../constants/countries';
import { SUPPORTED_CURRENCIES } from '../constants/currencies';
import { invalidateDestashDenormalizationCache } from '../services/destashDenormalization';

// Types for settings
interface Vouch {
  id: string;
  fromUserId: string;
  fromUsername: string;
  fromDisplayName: string;
  fromAvatarUrl?: string;
  createdAt: Timestamp;
}

// Section save message type
interface SectionMessage {
  type: 'success' | 'error';
  text: string;
}

export default function Settings() {
  const navigate = useNavigate();

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  
  // Section-specific saving states
  const [savingUsername, setSavingUsername] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [savingAppearance, setSavingAppearance] = useState(false);
  
  // Section-specific messages
  const [usernameMessage, setUsernameMessage] = useState<SectionMessage | null>(null);
  const [privacyMessage, setPrivacyMessage] = useState<SectionMessage | null>(null);
  const [appearanceMessage, setAppearanceMessage] = useState<SectionMessage | null>(null);

  // Username state (kept here for URL management)
  const [username, setUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameLastChanged, setUsernameLastChanged] = useState<Date | null>(null);
  const [canChangeUsername, setCanChangeUsername] = useState(true);
  const [daysUntilChange, setDaysUntilChange] = useState(0);

  // Privacy state
  const [isPublic, setIsPublic] = useState(false);
  const [showCollection, setShowCollection] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);
  const [showDestashList, setShowDestashList] = useState(false);
  const [showCreations, setShowCreations] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showDestashArchive, setShowDestashArchive] = useState(false);
  const [showRareLE, setShowRareLE] = useState(false);
  const [showSwatched, setShowSwatched] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [allowMessages, setAllowMessages] = useState(true);
  const [allowWishlistContact, setAllowWishlistContact] = useState(false); // OFF by default for privacy
  
  // Granular Collection Insights privacy
  const [showInsightsOverview, setShowInsightsOverview] = useState(true);
  const [showCollectionValue, setShowCollectionValue] = useState(false); // OFF by default
  const [showCollectorBadges, setShowCollectorBadges] = useState(true);
  const [showCategoryStats, setShowCategoryStats] = useState(true);
  const [showPolishBrandTypes, setShowPolishBrandTypes] = useState(true);

  // Shipping state (for marketplace)
  const [location, setLocation] = useState('');
  const [shipsTo, setShipsTo] = useState('');
  // Structured shipping + currency added Jul 2026 — see
  // src/constants/countries.ts + currencies.ts. The free-text `location`
  // + `shipsTo` above are preserved for legacy display but are
  // populated from the structured values on save.
  const [shipsFromCountry, setShipsFromCountry] = useState<string>('');
  const [shipsToCountries, setShipsToCountries] = useState<string[]>([]);
  const [shipsToWorldwide, setShipsToWorldwide] = useState(false);
  const [currency, setCurrency] = useState<string>('USD');
  const [savingShipping, setSavingShipping] = useState(false);
  const [shippingMessage, setShippingMessage] = useState<SectionMessage | null>(null);

  // Preferences state
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('light');
  const [defaultItemsPerPage, setDefaultItemsPerPage] = useState(20);

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Email change state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailPassword, setEmailPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailChanging, setEmailChanging] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deletionScheduledFor, setDeletionScheduledFor] = useState<Date | null>(null);

  // Vouches state
  const [vouches, setVouches] = useState<Vouch[]>([]);

  // Storage upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Email verification state
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  // Username change cooldown (30 days)
  const USERNAME_CHANGE_COOLDOWN_DAYS = 30;

  // Load user settings - wait for auth to be ready
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const data = userDoc.data();

          // Username
          if (data.profile?.username) {
            setUsername(data.profile.username);
            setOriginalUsername(data.profile.username);
          } else if (data.username) {
            setUsername(data.username);
            setOriginalUsername(data.username);
          }

          // Check username change cooldown
          if (data.usernameLastChanged) {
            const lastChanged = data.usernameLastChanged.toDate ? 
              data.usernameLastChanged.toDate() : 
              new Date(data.usernameLastChanged);
            setUsernameLastChanged(lastChanged);
            
            const daysSinceChange = Math.floor((Date.now() - lastChanged.getTime()) / (1000 * 60 * 60 * 24));
            const daysRemaining = USERNAME_CHANGE_COOLDOWN_DAYS - daysSinceChange;
            
            if (daysRemaining > 0) {
              setCanChangeUsername(false);
              setDaysUntilChange(daysRemaining);
            } else {
              setCanChangeUsername(true);
            }
          }

          // Privacy - check nested first, then root level
          if (data.privacy) {
            setIsPublic(data.privacy.isPublic || false);
            setShowCollection(data.privacy.showCollection || false);
            setShowWishlist(data.privacy.showWishlist || false);
            setShowDestashList(data.privacy.showDestashList || false);
            setShowCreations(data.privacy.showCreations || false);
            setShowFavorites(data.privacy.showFavorites || false);
            setShowDestashArchive(data.privacy.showDestashArchive || false);
            setShowRareLE(data.privacy.showRareLE || false);
            setShowSwatched(data.privacy.showSwatched || false);
            setShowProjects(data.privacy.showProjects || false);
            setShowStats(data.privacy.showStats || false);
            setAllowMessages(data.privacy.allowMessages !== false); // Default to true
            setAllowWishlistContact(data.privacy.allowWishlistContact || false); // Default to false
            
            // Granular Collection Insights settings
            setShowInsightsOverview(data.privacy.showInsightsOverview !== false); // Default to true
            setShowCollectionValue(data.privacy.showCollectionValue || false); // Default to false
            setShowCollectorBadges(data.privacy.showCollectorBadges !== false); // Default to true
            setShowCategoryStats(data.privacy.showCategoryStats !== false); // Default to true
            setShowPolishBrandTypes(data.privacy.showPolishBrandTypes !== false); // Default to true
          }
          
          // Check root-level isCollectionPublic
          if (data.isCollectionPublic !== undefined) {
            setIsPublic(data.isCollectionPublic);
            setShowCollection(data.isCollectionPublic);
          }

          // Shipping info (for marketplace)
          setLocation(data.location || data.profile?.location || '');
          setShipsTo(data.shipsTo || data.profile?.shipsTo || '');
          // Structured shipping + currency
          setShipsFromCountry(data.shipsFromCountry || '');
          setShipsToCountries(Array.isArray(data.shipsToCountries) ? data.shipsToCountries : []);
          setShipsToWorldwide(data.shipsToWorldwide === true);
          setCurrency(data.currency || 'USD');

          // Preferences
          if (data.preferences) {
            setTheme(data.preferences.theme || 'light');
            setDefaultItemsPerPage(data.preferences.defaultItemsPerPage || 20);
          }

          // Load vouches for this user
          const vouchesRef = collection(db, 'users', user.uid, 'vouches');
          const vouchesSnapshot = await getDocs(vouchesRef);
          const loadedVouches: Vouch[] = [];
          vouchesSnapshot.forEach((doc) => {
            loadedVouches.push({ id: doc.id, ...doc.data() } as Vouch);
          });
          // Sort by most recent first
          loadedVouches.sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(0);
            const dateB = b.createdAt?.toDate?.() || new Date(0);
            return dateB.getTime() - dateA.getTime();
          });
          setVouches(loadedVouches);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Username validation
  const validateUsername = (value: string): string => {
    if (value.length < 3) {
      return 'Username must be at least 3 characters';
    }
    if (value.length > 30) {
      return 'Username must be 30 characters or less';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return 'Username can only contain letters, numbers, and underscores';
    }
    return '';
  };

  // Check username availability
  useEffect(() => {
    const checkUsername = async () => {
      const user = getCurrentUser();
      if (!user) return;
      
      // Skip if same as original
      if (username.toLowerCase() === originalUsername.toLowerCase()) {
        setUsernameAvailable(true);
        setUsernameError('');
        return;
      }

      // Skip if can't change username (cooldown)
      if (!canChangeUsername) {
        return;
      }

      const error = validateUsername(username);
      if (error) {
        setUsernameError(error);
        setUsernameAvailable(null);
        return;
      }

      setUsernameChecking(true);
      setUsernameError('');

      try {
        // Check the usernames collection
        const usernameDocRef = doc(db, 'usernames', username.toLowerCase());
        const usernameDoc = await getDoc(usernameDocRef);

        if (usernameDoc.exists()) {
          const docData = usernameDoc.data();
          const existingUserId = docData.userId || docData.uid;
          
          if (existingUserId !== user.uid) {
            setUsernameAvailable(false);
            setUsernameError('Username is already taken');
          } else {
            setUsernameAvailable(true);
          }
        } else {
          setUsernameAvailable(true);
        }
      } catch (error) {
        console.error('Error checking username:', error);
      } finally {
        setUsernameChecking(false);
      }
    };

    const debounce = setTimeout(checkUsername, 500);
    return () => clearTimeout(debounce);
  }, [username, originalUsername, canChangeUsername]);

  // Save username only
  const handleSaveUsername = async () => {
    const user = getCurrentUser();
    if (!user) return;

    const usernameChanged = username.toLowerCase() !== originalUsername.toLowerCase();
    
    if (!usernameChanged) {
      setUsernameMessage({ type: 'success', text: 'No changes to save' });
      setTimeout(() => setUsernameMessage(null), 2000);
      return;
    }

    if (!canChangeUsername) {
      setUsernameMessage({ type: 'error', text: `You can change your username in ${daysUntilChange} days` });
      return;
    }
    
    if (usernameAvailable === false || usernameError) {
      setUsernameMessage({ type: 'error', text: 'Please fix username errors before saving' });
      return;
    }

    setSavingUsername(true);
    setUsernameMessage(null);

    try {
      // Reserve the new username
      const usernameDocRef = doc(db, 'usernames', username.toLowerCase());
      await setDoc(usernameDocRef, { 
        uid: user.uid,
        reservedAt: new Date()
      });

      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        username,
        'profile.username': username,
        'profile.usernameLower': username.toLowerCase(),
        usernameLastChanged: Timestamp.now()
      }, { merge: true });

      setOriginalUsername(username);
      setUsernameLastChanged(new Date());
      setCanChangeUsername(false);
      setDaysUntilChange(USERNAME_CHANGE_COOLDOWN_DAYS);
      
      setUsernameMessage({ type: 'success', text: 'Username saved!' });
      setTimeout(() => setUsernameMessage(null), 3000);
    } catch (error) {
      console.error('Error saving username:', error);
      setUsernameMessage({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setSavingUsername(false);
    }
  };

  // Save privacy settings only
  const handleSavePrivacy = async () => {
    const user = getCurrentUser();
    if (!user) return;

    setSavingPrivacy(true);
    setPrivacyMessage(null);

    try{
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        isCollectionPublic: isPublic,
        privacy: {
          isPublic,
          showCollection,
          showWishlist,
          showDestashList,
          showCreations,
          showFavorites,
          showDestashArchive,
          showRareLE,
          showSwatched,
          showProjects,
          showStats,
          allowMessages,
          allowWishlistContact,
          // Granular Collection Insights settings
          showInsightsOverview,
          showCollectionValue,
          showCollectorBadges,
          showCategoryStats,
          showPolishBrandTypes
        }
      }, { merge: true });

      setPrivacyMessage({ type: 'success', text: 'Privacy settings saved!' });
      setTimeout(() => setPrivacyMessage(null), 3000);
    } catch (error) {
      console.error('Error saving privacy:', error);
      setPrivacyMessage({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setSavingPrivacy(false);
    }
  };

  // Save shipping settings only
  const handleSaveShipping = async () => {
    const user = getCurrentUser();
    if (!user) return;

    setSavingShipping(true);
    setShippingMessage(null);

    try {
      const userDocRef = doc(db, 'users', user.uid);
      // Derive a human-readable `shipsTo` mirror from the structured
      // fields so legacy marketplace filters + display continue to
      // work while we transition. Worldwide → "Worldwide"; specific
      // countries → their names joined; empty → keep whatever the
      // user typed in the free-text field (fallback).
      const derivedShipsTo = (() => {
        if (shipsToWorldwide) return 'Worldwide';
        if (shipsToCountries.length > 0) {
          const names = shipsToCountries
            .map((c) => SUPPORTED_COUNTRIES.find((sc) => sc.code === c)?.name)
            .filter(Boolean) as string[];
          return names.join(', ');
        }
        return shipsTo || null;
      })();
      await setDoc(userDocRef, {
        // Legacy free-text mirrors — kept in sync with structured
        // values so old readers continue to work.
        location: location || null,
        shipsTo: derivedShipsTo,
        // Structured — the new source of truth.
        shipsFromCountry: shipsFromCountry || null,
        shipsToCountries: shipsToCountries.length > 0 ? shipsToCountries : null,
        shipsToWorldwide: shipsToWorldwide === true,
        currency: currency || 'USD',
      }, { merge: true });

      // Invalidate the destashDenormalization cache so the next
      // destash toggle in this session picks up the new values
      // without needing a page refresh.
      invalidateDestashDenormalizationCache(user.uid);

      setShippingMessage({ type: 'success', text: 'Shipping info saved!' });
      setTimeout(() => setShippingMessage(null), 3000);
    } catch (error) {
      console.error('Error saving shipping:', error);
      setShippingMessage({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setSavingShipping(false);
    }
  };

  // Save appearance settings only
  const handleSaveAppearance = async () => {
    const user = getCurrentUser();
    if (!user) return;

    setSavingAppearance(true);
    setAppearanceMessage(null);

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        preferences: {
          theme,
          defaultItemsPerPage
        }
      }, { merge: true });

      setAppearanceMessage({ type: 'success', text: 'Appearance settings saved!' });
      setTimeout(() => setAppearanceMessage(null), 3000);
    } catch (error) {
      console.error('Error saving appearance:', error);
      setAppearanceMessage({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setSavingAppearance(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    const user = getCurrentUser();
    if (!user || !user.email) return;

    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (error: any) {
      if (error.code === 'auth/wrong-password') {
        setPasswordError('Current password is incorrect');
      } else {
        setPasswordError('Failed to change password. Please try again.');
      }
    }
  };

  // Change email
  const handleChangeEmail = async () => {
    setEmailError('');
    setEmailSuccess(false);
    setEmailChanging(true);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newEmail || !emailRegex.test(newEmail)) {
      setEmailError('Please enter a valid email address');
      setEmailChanging(false);
      return;
    }

    const user = getCurrentUser();
    if (!user || !user.email) {
      setEmailError('No user logged in');
      setEmailChanging(false);
      return;
    }

    if (newEmail.toLowerCase() === user.email.toLowerCase()) {
      setEmailError('New email is the same as current email');
      setEmailChanging(false);
      return;
    }

    try {
      // Re-authenticate user first
      const credential = EmailAuthProvider.credential(user.email, emailPassword);
      await reauthenticateWithCredential(user, credential);

      // Send verification email to new address
      // User must click link in email to complete the change
      await verifyBeforeUpdateEmail(user, newEmail);

      setEmailSuccess(true);
      setEmailPassword('');
      setNewEmail('');
      // Don't auto-close - user needs to see the instructions
    } catch (error: any) {
      console.error('Error changing email:', error);
      if (error.code === 'auth/wrong-password') {
        setEmailError('Password is incorrect');
      } else if (error.code === 'auth/email-already-in-use') {
        setEmailError('This email is already in use by another account');
      } else if (error.code === 'auth/invalid-email') {
        setEmailError('Invalid email address');
      } else if (error.code === 'auth/requires-recent-login') {
        setEmailError('Please sign out and sign back in, then try again');
      } else {
        setEmailError('Failed to change email. Please try again.');
      }
    } finally {
      setEmailChanging(false);
    }
  };

  // Delete account
  const handleExportData = async () => {
    if (exporting) return;
    setExporting(true);
    setExportMsg(null);
    try {
      const call = httpsCallable(functions, 'requestDataExport');
      const res: any = await call();
      setExportMsg(
        res?.data?.already
          ? 'An export is already being prepared — check your email shortly.'
          : `We're preparing your data and photos, and we'll email a download link to ${auth.currentUser?.email}. Large collections can take a few minutes.`
      );
    } catch (e: any) {
      setExportMsg(e?.message ?? 'Could not start the export. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Detect a pending account deletion so we can offer to reactivate.
  useEffect(() => {
    const user = getCurrentUser();
    if (!user) return;
    getDoc(doc(db, 'users', user.uid))
      .then((snap) => {
        const when = snap.exists() ? (snap.data().deletionScheduledFor as any)?.toDate?.() : null;
        setDeletionScheduledFor(when && when.getTime() > Date.now() ? when : null);
      })
      .catch(() => {});
  }, []);

  const handleReactivate = async () => {
    const user = getCurrentUser();
    if (!user) return;
    await updateDoc(doc(db, 'users', user.uid), {
      deletionScheduledFor: deleteField(),
      deletionRequestedAt: deleteField(),
    });
    setDeletionScheduledFor(null);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }

    const user = getCurrentUser();
    if (!user || !user.email) return;

    try {
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);

      // Schedule deletion 30 days out — the purgeScheduledDeletions Cloud
      // Function permanently removes the account + data once elapsed. Signing
      // back in before then offers to reactivate.
      const when = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      await setDoc(
        doc(db, 'users', user.uid),
        { deletionScheduledFor: Timestamp.fromDate(when), deletionRequestedAt: Timestamp.now() },
        { merge: true }
      );

      await signOut(auth);
      navigate('/');
    } catch (error: any) {
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setDeleteError('Incorrect password');
      } else {
        setDeleteError('Failed to schedule deletion. Please try again.');
      }
    }
  };

  // Sign out
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Reusable save button component
  const SaveButton = ({ 
    onClick, 
    saving, 
    disabled = false,
    message 
  }: { 
    onClick: () => void; 
    saving: boolean; 
    disabled?: boolean;
    message: SectionMessage | null;
  }) => (
    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-600">
      <button
        onClick={onClick}
        disabled={saving || disabled}
        className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-default font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-body"
      >
        {saving ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Check className="icon-sm" />
            Save
          </>
        )}
      </button>
      {message && (
        <span className={`text-sm font-medium ${
          message.type === 'success' ? 'text-green-600' : 'text-red-600'
        }`}>
          {message.text}
        </span>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-on-bg text-xl">Loading settings...</div>
      </div>
    );
  }

  // Get user for rendering (will be available after loading completes)
  const user = getCurrentUser();

  return (
    <div className="py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={SettingsIcon}
          title="Settings"
          subtitle="Manage your account, privacy, and preferences"
          className="mb-6"
          actions={
            username ? (
              <Link
                to={`/profile/${username}`}
                className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-slate-700/50 transition-default font-medium font-body"
              >
                View Profile
              </Link>
            ) : undefined
          }
        />

        {deletionScheduledFor && (
          <div className="glass-card p-6 mb-6 border border-red-500/50">
            <h2 className="text-lg font-bold text-red-400 mb-1 font-body">
              ⚠️ Account scheduled for deletion
            </h2>
            <p className="text-glass-secondary text-sm mb-4">
              Your account and all your data are set to be permanently deleted on{' '}
              <strong>{deletionScheduledFor.toLocaleDateString()}</strong>. Reactivate to cancel.
            </p>
            <button
              onClick={handleReactivate}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-medium"
            >
              Reactivate my account
            </button>
          </div>
        )}

        {/* Username Section */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            🔗 Your Username
          </h2>
          
          <p className="text-glass-muted text-sm mb-4">
            This is your unique identifier and appears in your profile URL.
          </p>

          <div className="space-y-4">
            {/* Username Input */}
            <div>
              <label className="text-sm font-medium text-glass-secondary block mb-1">Username</label>
              <div className="flex items-center gap-2">
                <span className="text-glass-muted">@</span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                  disabled={!canChangeUsername}
                  className={`flex-grow px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    !canChangeUsername
                      ? 'bg-slate-800/40 text-glass-muted cursor-not-allowed'
                      : usernameError 
                        ? 'border-red-300 focus:ring-red-500' 
                        : usernameAvailable 
                          ? 'border-green-300 focus:ring-green-500' 
                          : 'border-slate-600 focus:ring-slate-500'
                  }`}
                  maxLength={30}
                />
                <span className="text-sm">
                  {usernameChecking ? (
                    <span className="text-glass-muted">Checking...</span>
                  ) : usernameError ? (
                    <span className="text-red-500">❌</span>
                  ) : usernameAvailable && canChangeUsername ? (
                    <span className="text-green-500">✓ Available</span>
                  ) : null}
                </span>
              </div>
              {usernameError && (
                <p className="text-red-500 text-sm mt-1">{usernameError}</p>
              )}
              <p className="text-glass-muted text-xs mt-1">
                Your public URLs: polishallthethings.com/profile/{username || 'username'}
              </p>
            </div>

            {/* Cooldown Warning */}
            {!canChangeUsername && (
              <div className="flex items-start gap-3 p-4 bg-slate-800/60 border border-amber-500 rounded-lg">
                <AlertCircle className="icon-md text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-300 font-medium">Username change on cooldown</p>
                  <p className="text-amber-200 text-sm">
                    You can change your username again in <strong>{daysUntilChange} day{daysUntilChange !== 1 ? 's' : ''}</strong>.
                    {usernameLastChanged && (
                      <span className="block text-xs mt-1 text-amber-200/80">
                        Last changed: {usernameLastChanged.toLocaleDateString()}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <SaveButton 
            onClick={handleSaveUsername} 
            saving={savingUsername}
            disabled={!canChangeUsername || usernameError !== '' || usernameAvailable === false}
            message={usernameMessage}
          />
        </div>

        {/* Storage & Plan Section */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            📦 Storage & Plan
          </h2>
          
          <p className="text-glass-muted text-sm mb-4">
            Manage your photo storage and subscription plan. Your photos are stored securely and count toward your storage limit.
          </p>
          
          <StorageUsageCard
            showUpgradeButton={true}
            showDetails={true}
            onUpgradeClick={() => setShowUpgradeModal(true)}
          />
          
          <div className="mt-4 text-center">
            <Link
              to="/pricing"
              className="text-sm text-glass-secondary hover:text-glass hover:underline transition-default"
            >
              Compare all plans →
            </Link>
          </div>
        </div>

        {/* Your Data Section */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            📥 Your Data
          </h2>
          <p className="text-glass-muted text-sm mb-4">
            Download everything — your full collection data (re-importable JSON plus a spreadsheet)
            and every photo you've uploaded. We'll build a zip and email you a download link.
          </p>
          {exportMsg && (
            <div className="mb-4 p-3 rounded-lg bg-slate-800/60 text-glass-secondary text-sm">
              {exportMsg}
            </div>
          )}
          <button
            onClick={handleExportData}
            disabled={exporting}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {exporting && <Loader2 className="w-4 h-4 animate-spin" />}
            {exporting ? 'Starting…' : 'Download my data'}
          </button>
        </div>

        {/* Privacy Section */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            🔒 Privacy
          </h2>

          <div className="space-y-4">
            {/* Public/Private Toggle */}
            <div className="flex items-center gap-4 p-4 bg-slate-800/60 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  checked={!isPublic}
                  onChange={() => setIsPublic(false)}
                  className="w-4 h-4 text-glass"
                />
                <span className="font-medium text-glass">🔒 Private</span>
                <span className="text-glass-muted text-sm">- Only I can see my profile</span>
              </label>
            </div>
            <div className="flex items-center gap-4 p-4 bg-slate-800/60 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  checked={isPublic}
                  onChange={() => setIsPublic(true)}
                  className="w-4 h-4 text-glass"
                />
                <span className="font-medium text-glass">🌐 Public</span>
                <span className="text-glass-muted text-sm">- Others can visit my profile</span>
              </label>
            </div>

            {/* Profile Sections - Always visible, but grayed out when private */}
            <div className={`mt-4 p-4 border border-slate-600 rounded-lg ${!isPublic ? 'opacity-60' : ''}`}>
              <p className="font-medium text-glass-secondary mb-1">Profile Sections</p>
              <p className="text-sm text-glass-muted mb-3">
                {isPublic 
                  ? 'Check the boxes to display these sections on your public profile:'
                  : 'These settings will apply when your profile is set to public:'
                }
              </p>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showCollection}
                        onChange={(e) => setShowCollection(e.target.checked)}
                        className="w-4 h-4 text-glass rounded"
                      />
                      <span className="text-glass-secondary">✨ My Collection</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showWishlist}
                        onChange={(e) => setShowWishlist(e.target.checked)}
                        className="w-4 h-4 text-glass rounded"
                      />
                      <span className="text-glass-secondary">⭐ My Wishlist & Watched Items</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showFavorites}
                        onChange={(e) => setShowFavorites(e.target.checked)}
                        className="w-4 h-4 text-glass rounded"
                      />
                      <span className="text-glass-secondary">❤️ My Favorites (Treasures)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showRareLE}
                        onChange={(e) => setShowRareLE(e.target.checked)}
                        className="w-4 h-4 text-glass rounded"
                      />
                      <span className="text-glass-secondary">💎 My Rare/Limited Editions</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showSwatched}
                        onChange={(e) => setShowSwatched(e.target.checked)}
                        className="w-4 h-4 text-glass rounded"
                      />
                      <span className="text-glass-secondary">🎨 My Swatched Items</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showDestashList}
                        onChange={(e) => setShowDestashList(e.target.checked)}
                        className="w-4 h-4 text-glass rounded"
                      />
                      <span className="text-glass-secondary">🏷️ For Sale/Trade</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showDestashArchive}
                        onChange={(e) => setShowDestashArchive(e.target.checked)}
                        className="w-4 h-4 text-glass rounded"
                      />
                      <span className="text-glass-secondary">📦 Destash/Archive</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showCreations}
                        onChange={(e) => setShowCreations(e.target.checked)}
                        className="w-4 h-4 text-glass rounded"
                      />
                      <span className="text-glass-secondary">💅 My Creations (Manis, Pedis, etc.)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showProjects}
                        onChange={(e) => setShowProjects(e.target.checked)}
                        className="w-4 h-4 text-glass rounded"
                      />
                      <span className="text-glass-secondary">🎨 My Projects</span>
                    </label>
                  </div>
                </div>

                {/* Collection Insights Section */}
                <div className={`mt-4 p-4 border border-slate-600 rounded-lg ${!isPublic ? 'opacity-60' : ''}`}>
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={showStats}
                      onChange={(e) => setShowStats(e.target.checked)}
                      className="w-4 h-4 text-glass rounded"
                    />
                    <span className="font-medium text-glass-secondary">📊 Show Collection Insights</span>
                  </label>
                  
                  {/* Granular insights options - only show if showStats is enabled */}
                  {showStats && (
                    <div className="ml-6 pl-4 border-l-2 border-slate-600 space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showInsightsOverview}
                          onChange={(e) => setShowInsightsOverview(e.target.checked)}
                          className="w-4 h-4 text-glass rounded"
                        />
                        <span className="text-glass-secondary">📈 Overview (Active, Listed, Destashed)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showCollectionValue}
                          onChange={(e) => setShowCollectionValue(e.target.checked)}
                          className="w-4 h-4 text-glass rounded"
                        />
                        <span className="text-glass-secondary">💰 Collection Value</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showCollectorBadges}
                          onChange={(e) => setShowCollectorBadges(e.target.checked)}
                          className="w-4 h-4 text-glass rounded"
                        />
                        <span className="text-glass-secondary">🐉 Collector Tier Badges</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showCategoryStats}
                          onChange={(e) => setShowCategoryStats(e.target.checked)}
                          className="w-4 h-4 text-glass rounded"
                        />
                        <span className="text-glass-secondary">🏆 Category Stats (Top Brands, Most Used, etc.)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showPolishBrandTypes}
                          onChange={(e) => setShowPolishBrandTypes(e.target.checked)}
                          className="w-4 h-4 text-glass rounded"
                        />
                        <span className="text-glass-secondary">💅 Polish Brand Types (Indie/Boutique/Mainstream)</span>
                      </label>
                    </div>
                  )}
                </div>

            {/* Messaging Toggle - separate from visibility */}
            <div className="mt-4 p-4 border border-slate-600 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowMessages}
                  onChange={(e) => setAllowMessages(e.target.checked)}
                  className="w-5 h-5 text-glass rounded"
                />
                <div>
                  <span className="font-medium text-glass-secondary">💬 Allow Messages</span>
                  <p className="text-sm text-glass-muted">Let other users send you messages from your profile</p>
                </div>
              </label>
            </div>

            {/* PublicWishlist Contact Toggle */}
            <div className="mt-4 p-4 border border-slate-600 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowWishlistContact}
                  onChange={(e) => setAllowWishlistContact(e.target.checked)}
                  className="w-5 h-5 text-glass rounded"
                />
                <div>
                  <span className="font-medium text-glass-secondary">🦄 Allow Wishlist Contact</span>
                  <p className="text-sm text-glass-muted">Let sellers contact you about items on your public wishlist (ISO/DISO/Unicorn items)</p>
                </div>
              </label>
            </div>
          </div>

          {/* Save Button */}
          <SaveButton 
            onClick={handleSavePrivacy} 
            saving={savingPrivacy}
            message={privacyMessage}
          />
        </div>

        {/* Shipping & Currency Section (for Marketplace/Destash) */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            📦 Shipping & Currency
          </h2>
          <p className="text-sm text-glass-muted mb-4">
            Help buyers know if you can ship to them and what currency you accept. This info appears on your marketplace listings and drives the "ships to my country" filter.
          </p>

          <div className="space-y-5">
            {/* Ships From — structured country picker */}
            <div>
              <label className="block text-sm font-medium text-glass-secondary mb-1">
                Ships From
              </label>
              <select
                value={shipsFromCountry}
                onChange={(e) => {
                  const next = e.target.value;
                  setShipsFromCountry(next);
                  // Auto-suggest currency to match (user can still override below).
                  if (next && currency === 'USD') {
                    setCurrency(defaultCurrencyForCountry(next));
                  }
                }}
                className="w-full px-4 py-2 rounded-lg border border-slate-600 bg-slate-800/60 text-glass focus:outline-none focus:ring-2 focus:ring-purple-500 font-body"
              >
                <option value="">— Select country —</option>
                {Object.entries(groupCountriesByRegion()).map(([region, countries]) => (
                  <optgroup key={region} label={region}>
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-glass-muted mt-1">
                Your country of origin for shipping.
              </p>
            </div>

            {/* Optional free-text location for profile display */}
            <div>
              <label className="block text-sm font-medium text-glass-secondary mb-1">
                Display location <span className="text-glass-muted font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Rio Rancho, NM"
                className="w-full px-4 py-2 rounded-lg border border-slate-600 bg-slate-800/60 text-glass placeholder:text-glass-muted focus:outline-none focus:ring-2 focus:ring-purple-500 font-body"
              />
              <p className="text-xs text-glass-muted mt-1">
                City / state for your profile page. Not used for filtering.
              </p>
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-glass-secondary mb-1">
                Currency I accept
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-slate-600 bg-slate-800/60 text-glass focus:outline-none focus:ring-2 focus:ring-purple-500 font-body"
              >
                {SUPPORTED_CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <p className="text-xs text-glass-muted mt-1">
                All your destash listings display in this currency. Change it here, and the change cascades to your live listings.
              </p>
            </div>

            {/* Ships To */}
            <div>
              <label className="block text-sm font-medium text-glass-secondary mb-2">
                Ships To
              </label>

              {/* Worldwide toggle */}
              <label className="flex items-start gap-2 mb-3 cursor-pointer text-glass">
                <input
                  type="checkbox"
                  checked={shipsToWorldwide}
                  onChange={(e) => setShipsToWorldwide(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">🌍 Worldwide</span>
                  <span className="block text-xs text-glass-muted">
                    Check this if you ship anywhere. Country checkboxes below are ignored.
                  </span>
                </span>
              </label>

              {/* Country grid */}
              <div className={`grid grid-cols-2 md:grid-cols-3 gap-2 ${shipsToWorldwide ? 'opacity-50 pointer-events-none' : ''}`}>
                {Object.entries(groupCountriesByRegion()).map(([region, countries]) => (
                  <div key={region} className="col-span-2 md:col-span-3">
                    <div className="text-xs uppercase text-glass-muted mt-2 mb-1 font-semibold">{region}</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                      {countries.map((c) => (
                        <label key={c.code} className="flex items-center gap-1.5 text-sm text-glass cursor-pointer">
                          <input
                            type="checkbox"
                            checked={shipsToCountries.includes(c.code)}
                            disabled={shipsToWorldwide}
                            onChange={(e) => {
                              if (e.target.checked) setShipsToCountries((prev) => [...prev, c.code]);
                              else setShipsToCountries((prev) => prev.filter((code) => code !== c.code));
                            }}
                          />
                          <span>{c.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-glass-muted mt-2">
                Pick every country you'll ship to, or check Worldwide above. If you leave both empty, your listings show only in your Ships From country.
              </p>
            </div>

            {/* Optional legacy free-text ships-to for niche cases */}
            <div>
              <label className="block text-sm font-medium text-glass-secondary mb-1">
                Extra shipping notes <span className="text-glass-muted font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={shipsTo}
                onChange={(e) => setShipsTo(e.target.value)}
                placeholder="e.g., US & neighboring EU countries; contact for others"
                className="w-full px-4 py-2 rounded-lg border border-slate-600 bg-slate-800/60 text-glass placeholder:text-glass-muted focus:outline-none focus:ring-2 focus:ring-purple-500 font-body"
              />
              <p className="text-xs text-glass-muted mt-1">
                Anything the structured picker above can't capture. Legacy field — kept for existing users' info.
              </p>
            </div>
          </div>

          {/* Save Button */}
          <SaveButton
            onClick={handleSaveShipping}
            saving={savingShipping}
            message={shippingMessage}
          />
        </div>

        {/* Vouches Section */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            💜 People Who Vouched for You
          </h2>

          {vouches.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-5xl mb-3">🤝</div>
              <p className="text-glass-muted font-body">
                No vouches yet. When other collectors vouch for you after successful trades, they'll appear here!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-glass-secondary mb-4">
                You have <span className="font-bold text-purple-400">{vouches.length}</span> {vouches.length === 1 ? 'vouch' : 'vouches'} from other collectors!
              </p>
              {vouches.map((vouch) => (
                <Link
                  key={vouch.id}
                  to={`/profile/${vouch.fromUsername || vouch.fromUserId}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/60 hover:bg-slate-700 transition-default"
                >
                  {vouch.fromAvatarUrl ? (
                    <img
                      src={vouch.fromAvatarUrl}
                      alt={vouch.fromDisplayName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-fuchsia-500 flex items-center justify-center text-white text-sm font-bold">
                      {vouch.fromDisplayName?.charAt(0).toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-glass font-body">
                      {vouch.fromDisplayName}
                    </div>
                    {vouch.fromUsername && (
                      <div className="text-sm text-glass-muted">@{vouch.fromUsername}</div>
                    )}
                  </div>
                  <div className="text-xs text-glass-muted">
                    {vouch.createdAt?.toDate?.().toLocaleDateString() || ''}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Appearance Section */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            🎨 Appearance
          </h2>

          <div className="space-y-4">
            {/* Theme */}
            <div>
              <label className="text-sm font-medium text-glass-secondary block mb-2">Theme</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="theme"
                    checked={theme === 'light'}
                    onChange={() => setTheme('light')}
                    className="w-4 h-4 text-glass"
                  />
                  <span className="text-glass-secondary">☀️ Light</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="theme"
                    checked={theme === 'dark'}
                    onChange={() => setTheme('dark')}
                    className="w-4 h-4 text-glass"
                  />
                  <span className="text-glass-secondary">🌙 Dark</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="theme"
                    checked={theme === 'system'}
                    onChange={() => setTheme('system')}
                    className="w-4 h-4 text-glass"
                  />
                  <span className="text-glass-secondary">💻 System</span>
                </label>
              </div>
            </div>

            {/* Items per page */}
            <div>
              <label className="text-sm font-medium text-glass-secondary block mb-2">Default items per page</label>
              <select
                value={defaultItemsPerPage}
                onChange={(e) => setDefaultItemsPerPage(Number(e.target.value))}
                className="px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
              >
                <option value={20}>20 items</option>
                <option value={50}>50 items</option>
                <option value={100}>100 items</option>
              </select>
            </div>
          </div>

          {/* Save Button */}
          <SaveButton 
            onClick={handleSaveAppearance} 
            saving={savingAppearance}
            message={appearanceMessage}
          />
        </div>

        {/* Notifications Section */}
        <NotificationSettings />

        {/* Push Notifications Section */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            🔔 Push Notifications
          </h2>
          <p className="text-glass-secondary text-sm mb-4 font-body">
            Get instant notifications in your browser when you receive messages, wishlist items become available, or someone's interested in your destash.
          </p>
          <NotificationPrompt
            variant="compact"
            onEnabled={() => {}}
          />
        </div>


        {/* Account Section */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2 font-body">
            🔐 Account
          </h2>

          <div className="space-y-4">
            {/* Email with Change Button */}
            <div>
              <label className="text-sm font-medium text-glass-secondary block mb-1">Email</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="flex-1 px-3 py-2 border border-slate-600 rounded-lg bg-slate-800/60 text-glass-muted"
                />
                <button
                  onClick={() => setShowEmailModal(true)}
                  className="px-3 py-2 bg-slate-800/40 text-glass-secondary rounded-lg hover:bg-slate-700 transition-default font-medium text-sm whitespace-nowrap"
                >
                  ✏️ Change
                </button>
              </div>
            </div>

            {/* Email Verification Status */}
            {user && !user.emailVerified && (
              <div className="p-4 bg-amber-500/20 border border-amber-400/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <span className="text-xl">⚠️</span>
                  <div className="flex-1">
                    <p className="text-amber-200 font-medium font-body">Email not verified</p>
                    <p className="text-amber-200/80 text-sm mt-1 font-body">
                      Please check your inbox (and spam folder) for a verification email.
                    </p>

                    {verificationSent ? (
                      <p className="text-green-400 text-sm mt-2 font-body">
                        ✓ Verification email sent! Check your inbox.
                      </p>
                    ) : verificationError ? (
                      <p className="text-red-400 text-sm mt-2 font-body">
                        {verificationError}
                      </p>
                    ) : null}

                    <button
                      onClick={async () => {
                        setIsResendingVerification(true);
                        setVerificationError('');
                        setVerificationSent(false);
                        try {
                          await sendVerificationEmail();
                          setVerificationSent(true);
                        } catch (error: any) {
                          console.error('Error sending verification email:', error);
                          setVerificationError(error.message || 'Failed to send. Please try again later.');
                        } finally {
                          setIsResendingVerification(false);
                        }
                      }}
                      disabled={isResendingVerification || verificationSent}
                      className="mt-2 text-sm text-amber-300 hover:text-white underline font-body disabled:opacity-50 disabled:no-underline"
                    >
                      {isResendingVerification ? 'Sending...' : verificationSent ? 'Email sent!' : 'Resend verification email'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {user && user.emailVerified && (
              <div className="flex items-center gap-2 text-green-400 text-sm font-body">
                <span>✓</span>
                <span>Email verified</span>
              </div>
            )}

            {/* Change Password Button */}
            <button
              onClick={() => setShowPasswordModal(true)}
              className="px-4 py-2 bg-slate-800/40 text-glass-secondary rounded-lg hover:bg-slate-700 transition-default font-medium"
            >
              🔑 Change Password
            </button>

            {/* Sign Out Button */}
            <button
              onClick={handleSignOut}
              className="px-4 py-2 bg-slate-700 text-glass rounded-lg hover:bg-slate-600 transition-default font-medium"
            >
              🚪 Sign Out
            </button>

            {/* Danger Zone */}
            <div className="mt-6 pt-6 border-t border-slate-600">
              <p className="text-red-600 font-medium mb-2">⚠️ Danger Zone</p>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 bg-slate-800/60 text-red-400 rounded-lg hover:bg-slate-700 transition-default font-medium border border-red-500"
              >
                🗑️ Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-glass mb-4">🔑 Change Password</h3>

            {passwordSuccess ? (
              <div className="text-center py-8">
                <div className="text-green-500 text-5xl mb-4">✓</div>
                <p className="text-green-600 font-medium">Password changed successfully!</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-glass-secondary block mb-1">Current Password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-glass-secondary block mb-1">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-glass-secondary block mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                    />
                  </div>

                  {passwordError && (
                    <p className="text-red-500 text-sm">{passwordError}</p>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                    }}
                    className="flex-1 px-4 py-2 bg-slate-800/40 text-glass-secondary rounded-lg hover:bg-slate-700 transition-default font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-default font-medium"
                  >
                    Change Password
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Change Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-glass mb-4">✉️ Change Email</h3>

            {emailSuccess ? (
              <div className="text-center py-4">
                <div className="text-green-500 text-5xl mb-4">✓</div>
                <p className="text-green-600 font-medium mb-2">Verification email sent!</p>
                <p className="text-glass-secondary text-sm">
                  Check your inbox at <span className="font-medium">{newEmail || 'your new email'}</span> and click the verification link to complete the change.
                </p>
                <p className="text-glass-muted text-xs mt-3">
                  Your email won't change until you verify the new address.
                </p>
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailSuccess(false);
                  }}
                  className="mt-4 px-4 py-2 bg-slate-800/40 text-glass-secondary rounded-lg hover:bg-slate-700 transition-default font-medium"
                >
                  Got it
                </button>
              </div>
            ) : (
              <>
                <p className="text-glass-secondary text-sm mb-4">
                  We'll send a verification link to your new email address. Your email won't change until you click that link.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-glass-secondary block mb-1">Current Email</label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-3 py-2 border border-slate-600 rounded-lg bg-slate-800/60 text-glass-muted"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-glass-secondary block mb-1">New Email</label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                      placeholder="your.new@email.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-glass-secondary block mb-1">Password (to confirm it's you)</label>
                    <input
                      type="password"
                      value={emailPassword}
                      onChange={(e) => setEmailPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
                      placeholder="Enter your current password"
                    />
                  </div>

                  {emailError && (
                    <p className="text-red-500 text-sm">{emailError}</p>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEmailModal(false);
                      setNewEmail('');
                      setEmailPassword('');
                      setEmailError('');
                    }}
                    className="flex-1 px-4 py-2 bg-slate-800/40 text-glass-secondary rounded-lg hover:bg-slate-700 transition-default font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangeEmail}
                    disabled={emailChanging || !newEmail || !emailPassword}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {emailChanging ? 'Sending...' : 'Send Verification'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-600 mb-2">⚠️ Delete Account</h3>
            <p className="text-glass-secondary mb-4">
              This schedules your account for permanent deletion in <strong>30 days</strong>. Your
              data stays until then, and you can cancel anytime by signing back in.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-glass-secondary block mb-1">
                  Type <span className="font-bold text-red-600">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="DELETE"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-glass-secondary block mb-1">Enter your password</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {deleteError && (
                <p className="text-red-500 text-sm">{deleteError}</p>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                  setDeletePassword('');
                  setDeleteError('');
                }}
                className="flex-1 px-4 py-2 bg-slate-800/40 text-glass-secondary rounded-lg hover:bg-slate-700 transition-default font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE'}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-default font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Schedule deletion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Storage Upgrade Modal */}
      {showUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onSelectPlan={(tierId: StorageTierId, billingCycle: 'monthly' | 'yearly') => {
            // TODO: Implement Stripe checkout
            console.log('Selected plan:', tierId, billingCycle);
            setShowUpgradeModal(false);
          }}
        />
      )}
    </div>
  );
}
